import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTranslation } from 'react-i18next';
import styles from './IdeaPdfViewer.module.css';
import Button from '../../../UI/Button/Button';
import IdeaFileUpdateForm from '../IdeaFileUpdateForm/IdeaFileUpdateForm';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { FiMaximize, FiMinimize, FiUpload } from 'react-icons/fi';

// Set up the worker for react-pdf (using the same version as the package)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function IdeaPdfViewer({ fileData, isLoading, ideaId, onUpdateSuccess, locked = false, onLocked, isOwner = false }) {
    const { t } = useTranslation('common');
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

    const data = fileData?.data;

    // Create a Blob URL for the PDF data to avoid DataCloneError when sharing buffer
    const pdfUrl = useMemo(() => {
        if (!data) return null;
        try {
            const binaryString = atob(data);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'application/pdf' });
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error('Error decoding PDF data:', error);
            return null;
        }
    }, [data]);

    // Clean up the Blob URL when data changes or component unmounts
    useEffect(() => {
        return () => {
            if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        };
    }, [pdfUrl]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isFullscreen) {
                setIsFullscreen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen]);

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
    }

    const goToPrevPage = (e) => {
        e.stopPropagation();
        setPageNumber(prev => Math.max(prev - 1, 1));
    };

    const goToNextPage = (e) => {
        e.stopPropagation();
        setPageNumber(prev => Math.min(prev + 1, numPages));
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    if (isLoading) {
        return (
            <div className={styles.previewContainer}>
                <h2 className={styles.sectionTitle}>{t('ideas.pdf.title')}</h2>
                <div className={styles.loadingText}>{t('ideas.pdf.loading')}</div>
            </div>
        );
    }

    if (!fileData || !data || !pdfUrl) return null;

    const { fileName, note } = fileData;

    return (
        <div className={styles.previewContainer}>
            <div className={styles.headerRow}>
                <h2 className={styles.sectionTitle}>{t('ideas.pdf.title')}</h2>
                <div className={styles.headerButtons}>
                    {!locked && isOwner && (
                        <Button variant="secondary" onClick={() => setIsUpdateModalOpen(true)} className={styles.updateButton}>
                            <FiUpload /> {t('ideas.pdf.update_file')}
                        </Button>
                    )}
                    <Button variant="secondary" onClick={toggleFullscreen} className={styles.fullscreenButton}>
                        <FiMaximize /> {t('ideas.pdf.fullscreen')}
                    </Button>
                </div>
            </div>

            {note && note.trim() !== '' && (
                <div className={styles.noteBox}>
                    <div className={styles.noteTitle}>{t('ideas.pdf.note')}</div>
                    <div className={styles.noteContent}>{note}</div>
                </div>
            )}

            <div className={styles.pdfWrapper}>
                <Document
                    key="main-doc"
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div className={styles.loadingText}>{t('ideas.pdf.loading')}</div>}
                >
                    <Page
                        pageNumber={pageNumber}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        width={Math.min(window.innerWidth - 80, 800)}
                    />
                </Document>

                {numPages > 1 && (
                    <div className={styles.pdfControls}>
                        <Button
                            variant="secondary"
                            onClick={goToPrevPage}
                            disabled={pageNumber <= 1}
                        >
                            <IoIosArrowBack />
                        </Button>
                        <span className={styles.pageInfo}>
                            {t('ideas.pdf.page')} {pageNumber} {t('ideas.pdf.of')} {numPages}
                        </span>
                        <Button
                            variant="secondary"
                            onClick={goToNextPage}
                            disabled={pageNumber >= numPages}
                        >
                            <IoIosArrowForward />
                        </Button>
                    </div>
                )}
            </div>

            <div className={styles.fileName}>{fileName}</div>

            {isFullscreen && createPortal(
                <div className={styles.fullscreenOverlay} onClick={toggleFullscreen}>
                    <div className={styles.fullscreenHeader} onClick={(e) => e.stopPropagation()}>
                        <span className={styles.fullscreenFileName}>{fileName}</span>
                        <Button variant="secondary" onClick={toggleFullscreen} className={styles.closeButton}>
                            <FiMinimize /> {t('ideas.pdf.exit_fullscreen')}
                        </Button>
                    </div>

                    <div className={styles.fullscreenContent} onClick={(e) => e.stopPropagation()}>
                        <Document
                            key="fullscreen-doc"
                            file={pdfUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={(err) => console.error('Full-screen PDF load error:', err)}
                            loading={<div className={styles.loadingText}>{t('ideas.pdf.loading')}</div>}
                        >
                            <Page
                                pageNumber={pageNumber}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                width={Math.min(window.innerWidth - 20, 1600)}
                            />
                        </Document>
                    </div>

                    {numPages > 1 && (
                        <div className={styles.fullscreenControls} onClick={(e) => e.stopPropagation()}>
                            <Button
                                variant="secondary"
                                onClick={goToPrevPage}
                                disabled={pageNumber <= 1}
                            >
                                <IoIosArrowBack />
                            </Button>
                            <span className={styles.fullscreenPageInfo}>
                                {pageNumber} / {numPages}
                            </span>
                            <Button
                                variant="secondary"
                                onClick={goToNextPage}
                                disabled={pageNumber >= numPages}
                            >
                                <IoIosArrowForward />
                            </Button>
                        </div>
                    )}
                </div>,
                document.body
            )}

            {isUpdateModalOpen && (
                <IdeaFileUpdateForm
                    isOpen={isUpdateModalOpen}
                    onClose={() => setIsUpdateModalOpen(false)}
                    onSuccess={onUpdateSuccess}
                    ideaId={ideaId}
                    locked={locked}
                    onLocked={onLocked}
                />
            )}
        </div>
    );
}

export default IdeaPdfViewer;
